import React from 'react';
import PropTypes from 'prop-types';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import DataContainer from 'Components/UI/DataContainer';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { useMessagesContext } from 'Views/Page/Content/Templates/MessagesPage/MessagesContext';
import { Box, Paper } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import classNames from 'classnames';
import {
  ComposeMessageRecipients,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ComposeMessage/components/ComposeMessageRecipients';
import useMobileLayout from 'Hooks/useMobileLayout';
import { DataValueMap } from 'Helpers/Testing';
import { MessageInput, useMessageInput } from 'Components/ComponentLibrary/Components/Uncategorized/MessageInput';
import {
  ComposeMessageSubject,
} from 'Views/Page/Content/Templates/MessagesPage/components/MessagesPageContent/components/ComposeMessage/components/ComposeMessageSubject';
import useUserContext from 'Context/UserContext';
import { ParentSize } from '@visx/responsive';

const useStyles = buildUseStyles('ComposeMessage', theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: theme.dimensions.lg,
    padding: theme.spacing(2),
    borderRadius: 0,
  },
  mobileSizeFallback: {
    width: '100vw',
    height: 'calc(100vh - 48px)',
  },
  mobile: {
    width: '100dvw',
    height: 'calc(100dvh - 48px)',
  },
  agentMobileSizeFallback: {
    height: 'calc(100vh - 96px)',
  },
  agentMobile: {
    height: 'calc(100dvh - 96px)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: 512, // So it doesn't shift when we are "Sending"
  },
  agentLoadingContainer: {
    height: 555, // So it doesn't shift when we are "Sending"
  },
  messageRecipients: {
    paddingLeft: theme.spacing(1),
  },
  messageSubject: {
    paddingLeft: theme.spacing(1),
  },
  mobileMessageInput: {
    flexGrow: 1,
  },
}));

const ComposeMessage = ({
  className,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const isMobile = useMobileLayout();
  const { isAgent } = useUserContext();
  const {
    composeDraftMessageInitializing,
    composeDraftMessageInitializingError,
    composeDraftMessageSending,
    composeDraftMessageResource,
    sendComposeDraft,
    refreshComposeDraftMessage,
    autoSaveComposeDraft,
    setComposeDraftMessageSubject,
  } = useMessagesContext();

  const {
    handleSubmit,
    handleContentChange,
    handleAttachmentDelete,
    handleTemplateInsert,
  } = useMessageInput(
    composeDraftMessageResource,
    sendComposeDraft,
    refreshComposeDraftMessage,
    autoSaveComposeDraft,
  );

  const handleSubjectChange = (subject) => {
    if (!subject) {
      return;
    }
    composeDraftMessageResource.attr('subject', subject);
    setComposeDraftMessageSubject(composeDraftMessageResource.attr('subject'));
    autoSaveComposeDraft(composeDraftMessageResource);
  };

  return (
    <DataContainer
      loading={composeDraftMessageInitializing || composeDraftMessageSending}
      loadingError={composeDraftMessageInitializingError}
      skeleton={() => (
        <Paper className={
          classNames(className, classes.root, classes.loadingContainer,
            {
              [classes.mobileSizeFallback]: isMobile,
              [classes.mobile]: isMobile,
              [classes.agentLoadingContainer]: isAgent,
            })}
        >
          <Typography variant={'h4'} color={'mediumGray'}>
            {composeDraftMessageSending ? 'Sending...' : 'Loading...'}
          </Typography>
        </Paper>
      )}
    >
      <Paper
        className={classNames(
          className,
          classes.root,
          {
            [classes.mobileSizeFallback]: isMobile,
            [classes.mobile]: isMobile,
            [classes.agentMobileSizeFallback]: isMobile && isAgent,
            [classes.agentMobile]: isMobile && isAgent,
          },
        )}
        {...DataValueMap.init('messages-page-compose-dock').build()}
      >
        <ComposeMessageRecipients className={classes.messageRecipients} />
        <ComposeMessageSubject className={classes.messageSubject} />
        <Box className={classNames(classes.input, { [classes.mobileMessageInput]: isMobile })}>
          <ParentSize ignoreDimensions={['height']}>
            {({ height }) => {
              // ParentHeight is causing CKEditor to render with 0 height, and it cannot
              // change after initial render. Therefor we want to wait until parent.height
              // is > 0 before rendering the message input

              if (isMobile && height === 0) return null;
              const toolbarHeight = isAgent ? 50 : 0;
              let mobileHeight = height - toolbarHeight;
              const hasAttachments = composeDraftMessageResource?.attr('attachments') !== undefined && composeDraftMessageResource.attr('attachments').resources()?.list().length > 0;
              if (isMobile && hasAttachments) {
                mobileHeight -= 60;
              }

              // this just asts to rerender so that the attachments, as well as the send button, show on mobile
              const resizeChildHeight = () => {};

              return (
                <MessageInput
                  onSubmit={(value) => handleSubmit(value)}
                  onChange={(value) => handleContentChange(value)}
                  onSubjectChange={(value) => handleSubjectChange(value)}
                  handleTemplateInsert={(value) => handleTemplateInsert(value)}
                  handleAttachmentDelete={handleAttachmentDelete}
                  onSetAttachments={resizeChildHeight}
                  messageSending={composeDraftMessageSending}
                  messageResource={composeDraftMessageResource}
                  refreshMessages={refreshComposeDraftMessage}
                  value={composeDraftMessageResource?.attr('content')}
                  dataTestAttributes={DataValueMap.init('messages-page-compose-dock-message-input').build()}
                  placeholder={'Write a new message'}
                  inputMaxHeight={isMobile ? mobileHeight : 600}
                  inputMinHeight={isMobile ? mobileHeight : 350}
                  borderless
                />
              );
            }}
          </ParentSize>
        </Box>
      </Paper>
    </DataContainer>
  );
};

ComposeMessage.defaultProps = {
  className: '',
};

ComposeMessage.propTypes = {
  className: PropTypes.string,
};

export { ComposeMessage };
